/**
 * Filter Registry Initialization
 *
 * Central place to register all filters.
 * To add a new filter category:
 * 1. Create a new file (e.g., myNewFilters.ts)
 * 2. Export const myNewFilters: Filter[] = [...]
 * 3. Import it here
 * 4. Add to registerMany()
 *
 * That's it! The UI will automatically render the new filters.
 */

import { filterRegistry } from '../../utils/filterRegistry';
import { financialFilters } from './financial';
import { propertyFilters } from './property';
import { locationFilters } from './location';
import { scoringFilters } from './scoring';
import { aiFilters } from './ai';

/**
 * Initialize all filters
 * This should be called once when the app starts
 */
export function initializeFilters(): void {
  // Prevent double initialization
  if (filterRegistry.isInitialized()) {
    return;
  }

  try {
    // Register all filter categories
    filterRegistry.registerMany([
      ...financialFilters,
      ...propertyFilters,
      ...locationFilters,
      ...scoringFilters,
      ...aiFilters
    ]);

    filterRegistry.setInitialized();
  } catch (error) {
    throw error;
  }
}

/**
 * Get all registered filters
 */
export function getAllFilters() {
  if (!filterRegistry.isInitialized()) {
    initializeFilters();
  }
  return filterRegistry.getAll();
}

/**
 * Example: To add a new filter category in the future:
 *
 * import { advancedFilters } from './advanced';
 *
 * filterRegistry.registerMany([
 *   ...financialFilters,
 *   ...propertyFilters,
 *   ...locationFilters,
 *   ...scoringFilters,
 *   ...aiFilters,
 *   ...advancedFilters  // New category!
 * ]);
 */

// Export the registry for direct access if needed
export { filterRegistry };
